import { type ReactNode, useMemo, useState } from "react";
import CompoundDetailPane from "@/components/reference/CompoundDetailPane";
import type { ReferenceCompoundItem } from "@/types/referenceCompound";

/**
 * 참조물질 DB 탐색 — 원본(NIST MS Search)의 **Names 화면** 이식 (S10 · 2026-08-19).
 *
 * 기획 v2 §1.3 의 배치를 따른다::
 *
 *     [검색어    ] [지우기] [a-z]                    N종
 *     ┌──────────────┬────────────────────────────────┐
 *     │ 이름 목록     │ 스펙트럼 + 2D 구조              │
 *     │ ▶선택        ├────────────────────────────────┤
 *     │              │ Name/Formula/CAS/Exact Mass/   │
 *     │              │ InChIKey/m/z 격자/Synonyms/RI  │
 *     └──────────────┴────────────────────────────────┘
 *
 * 이전 화면은 표 + 모달이었다. 원본이 **좌측 목록 / 우측 상세** 분할인 이유는
 * 물질을 위아래로 옮겨 가며 스펙트럼을 눈으로 비교하기 위해서다 — 모달은
 * 매번 닫고 열어야 해서 그 흐름이 끊긴다.
 *
 * **없는 것을 있는 것처럼 그리지 않는다.** 스펙트럼 미보유는 빈 차트가 아니라
 * "미보유"라고 말하고, Synonyms·RI 는 자료 미보유로 명시한다(중앙대 회신 대기).
 * `NIST#` 등 NIST 식별자는 만들지 않는다.
 */

/** 검색에서 무시하는 문자 — 백엔드 `SEARCH_IGNORED_CHARS` 와 같은 규칙. */
const IGNORED = /[\s,\-()[\]'"./+_;:]/g;

function normalize(term: string): string {
  return term.replace(IGNORED, "").toUpperCase();
}

interface ReferenceLibraryBrowserProps {
  items: ReferenceCompoundItem[];
  isLoading?: boolean;
  error?: Error | null;
  /**
   * 목록이 빈 이유. 탭마다 다르다 — 큐레이션 목록 미수신과 "검색 결과 없음"은
   * 다른 사실이라 화면이 같은 문구로 뭉개면 안 된다.
   */
  emptyState?: ReactNode;
}

export default function ReferenceLibraryBrowser({
  items,
  isLoading = false,
  error = null,
  emptyState,
}: ReferenceLibraryBrowserProps) {
  const [query, setQuery] = useState("");
  const [sortAz, setSortAz] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const filtered = useMemo(() => {
    const q = normalize(query);
    const list = q
      ? items.filter(
          (c) =>
            normalize(c.compound_name).includes(q) ||
            normalize(c.compound_name_ko ?? "").includes(q) ||
            normalize(c.cas_number ?? "").includes(q) ||
            normalize(c.molecular_formula ?? "").includes(q) ||
            normalize(c.smiles ?? "").includes(q),
        )
      : [...items];
    return sortAz
      ? list.sort((a, b) => a.compound_name.localeCompare(b.compound_name))
      : list;
  }, [items, query, sortAz]);

  // 선택이 목록 밖으로 밀리면(검색 등) 첫 항목을 **보여 주되**, 사용자의 선택은
  // 그대로 둔다. 종전에는 이 fallback 을 selectedId 에 써 넣어서, 검색어를 지워도
  // 원래 고른 물질로 돌아오지 못했다 (2026-08-20 교차 리뷰).
  const selected =
    filtered.find((c) => c.id === selectedId) ?? filtered[0] ?? null;

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2 p-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-10 rounded bg-gray-200" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-sm">
        <p className="font-medium text-red-600">
          데이터를 불러오는 중 오류가 발생했습니다.
        </p>
        <p className="mt-1 text-gray-500">{error.message}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="p-6 text-sm text-gray-500">
        {emptyState ?? "표시할 참조물질이 없습니다."}
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* 검색 줄 — 원본의 [검색어][Clear][a-z] */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="ref-search">
          물질 검색
        </label>
        <input
          id="ref-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="물질명 · 한글명 · CAS · 분자식 · SMILES (구두점 무시)"
          className="w-72 rounded border border-gray-300 px-2 py-1 text-sm"
        />
        <button
          type="button"
          onClick={() => setQuery("")}
          className="rounded border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-50"
        >
          지우기
        </button>
        <button
          type="button"
          onClick={() => setSortAz((v) => !v)}
          className={`rounded border px-2 py-1 text-xs ${
            sortAz
              ? "border-indigo-600 bg-indigo-600 text-white"
              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          }`}
        >
          a-z
        </button>
        <span className="ml-auto text-xs text-gray-500">
          {filtered.length}종 표시 / 불러온 {items.length}종
        </span>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* 좌: 이름 목록 */}
        <div className="col-span-12 lg:col-span-3">
          {/* ARIA 규격상 listbox 의 자식은 option 이어야 한다 — ul/li 를 끼우지
              않고 버튼을 직접 둔다. */}
          <div
            role="listbox"
            aria-label="참조물질 목록"
            className="max-h-[34rem] overflow-y-auto rounded border border-gray-200 text-sm"
          >
            {filtered.map((c) => {
              const isSelected = selected?.id === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => setSelectedId(c.id)}
                  className={`w-full border-b border-gray-100 px-3 py-1.5 text-left last:border-b-0 ${
                    isSelected ? "bg-indigo-600 text-white" : "hover:bg-gray-50"
                  }`}
                >
                  <span className="flex items-center gap-1">
                    <span className="min-w-0 flex-1 truncate">
                      {c.compound_name}
                    </span>
                    {c.is_ignitable_liquid_indicator && (
                      <span
                        title="인화성 액체(ILR) 지시성분으로 분류된 참조물질 — 분류 표시이며 검출·판정이 아니다"
                        className={`shrink-0 rounded-full px-1.5 text-[10px] font-medium ${
                          isSelected
                            ? "bg-white/20 text-white"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        인화성 지시
                      </span>
                    )}
                  </span>
                  {c.compound_name_ko && (
                    <span
                      className={`block truncate text-xs ${
                        isSelected ? "text-indigo-100" : "text-gray-500"
                      }`}
                    >
                      {c.compound_name_ko}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* 우: 상세 — 표준 라이브러리 탭과 **같은 패널**을 쓴다 (2026-08-20 통일) */}
        <div className="col-span-12 lg:col-span-9">
          <CompoundDetailPane
            dataset="reference"
            compound={
              selected
                ? {
                    name: selected.compound_name,
                    nameKo: selected.compound_name_ko,
                    formula: selected.molecular_formula,
                    casNumber: selected.cas_number,
                    compoundClass: selected.compound_class,
                    category: selected.category,
                    tier: selected.tier,
                    isIgnitableLiquidIndicator:
                      selected.is_ignitable_liquid_indicator,
                    smiles: selected.smiles,
                    peaks: selected.reference_spectrum?.peaks ?? [],
                    // 스펙트럼이 없으면 출처도 없다 — 같은 화면이 "미보유"라고
                    // 말하면서 출처를 적으면 서로 어긋난다 (교차 리뷰).
                    spectrumSource: (selected.reference_spectrum?.peaks ?? [])
                      .length
                      ? selected.source === "cau_standard_db"
                        ? "중앙대 표준물질 DB"
                        : selected.source
                      : null,
                  }
                : null
            }
          />
        </div>
      </div>
    </div>
  );
}
